import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Dumbbell, Trophy, Target, Image as ImageIcon,
  Loader2, Send, ChevronRight, Users, X
} from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/imageUtils';

export const CreatePostBox = ({
  user,
  postContent, setPostContent,
  postType, setPostType,
  posting, uploadingImage,
  hasRecentWorkout, hasAchievement, hasMilestone, recentWorkoutTitle,
  selectedImage, imagePreview,
  onImageSelect, onRemoveImage, onCreatePost,
}) => {
  const fileInputRef = useRef(null);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    onImageSelect(file);
  };

  const handleRemove = () => {
    onRemoveImage();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="mb-6"
    >
      <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-2">Create a Post</h3>
      <div className="bg-card border border-blue-500/40 p-3 sm:p-4 space-y-3 rounded-2xl electric-glow-blue">
        <div className="flex gap-2.5">
          <Avatar className="h-9 w-9 border-2 border-primary/30 flex-shrink-0">
            <AvatarImage src={getImageUrl(user?.profile_picture)} />
            <AvatarFallback className="bg-primary/20 text-primary text-sm">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <Textarea
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="Share your fitness journey..."
              className="bg-black border-border text-white text-sm min-h-[64px] resize-none focus:border-primary"
              data-testid="create-post-textarea"
            />
          </div>
        </div>

        <AnimatePresence>
          {imagePreview && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative"
            >
              <img src={imagePreview} alt="Preview" className="w-full max-h-48 object-cover rounded-lg border border-border" />
              <button
                onClick={handleRemove}
                className="absolute top-1.5 right-1.5 p-1 bg-black/70 hover:bg-black rounded-full transition-colors"
                data-testid="remove-image-btn"
              >
                <X className="h-3.5 w-3.5 text-white" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tag row — three equal chips on one line so they line up nicely on
            every screen size. Tiny label clarifies what they do. */}
        <div className="space-y-1.5">
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70">Tag your post</p>
          <div className="grid grid-cols-3 gap-1">
            <Button
              variant={postType === 'workout' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPostType(postType === 'workout' ? 'general' : 'workout')}
              className={`w-full min-w-0 flex-col items-center justify-center gap-0.5 text-[10px] px-1 py-1.5 h-auto overflow-hidden ${postType === 'workout' ? 'bg-primary text-black' : 'border-green-500/50 text-green-400 hover:border-green-400'}`}
              data-testid="post-type-workout"
              title={hasRecentWorkout && recentWorkoutTitle ? `Share your "${recentWorkoutTitle}" workout!` : 'Tag this post as a Workout'}
            >
              <Dumbbell className="h-3.5 w-3.5 shrink-0" />
              <span className="leading-none">Workout</span>
            </Button>

            <Button
              variant={postType === 'achievement' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPostType(postType === 'achievement' ? 'general' : 'achievement')}
              className={`w-full min-w-0 flex-col items-center justify-center gap-0.5 text-[10px] px-1 py-1.5 h-auto overflow-hidden ${postType === 'achievement' ? 'bg-primary text-black' : 'border-yellow-500/50 text-yellow-400 hover:border-yellow-400'}`}
              data-testid="post-type-achievement"
              title="Tag this post as an Achievement"
            >
              <Trophy className="h-3.5 w-3.5 shrink-0" />
              <span className="leading-none">Achievement</span>
            </Button>

            <Button
              variant={postType === 'milestone' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPostType(postType === 'milestone' ? 'general' : 'milestone')}
              className={`w-full min-w-0 flex-col items-center justify-center gap-0.5 text-[10px] px-1 py-1.5 h-auto overflow-hidden ${postType === 'milestone' ? 'bg-primary text-black' : 'border-blue-500/50 text-blue-400 hover:border-blue-400'}`}
              data-testid="post-type-milestone"
              title="Tag this post as a Milestone"
            >
              <Target className="h-3.5 w-3.5 shrink-0" />
              <span className="leading-none">Milestone</span>
            </Button>
          </div>
        </div>

        {/* Action row — Add Photo on the left, Post on the right. Same size so
            the bar feels balanced rather than the Post button floating alone. */}
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            className="hidden"
            data-testid="image-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImage}
            className="h-8 text-xs hover:border-primary/50"
            data-testid="add-image-btn"
          >
            {uploadingImage ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5 mr-1" />}
            Add Photo
          </Button>
          <Button
            onClick={onCreatePost}
            disabled={(!postContent.trim() && !selectedImage) || posting}
            className="h-8 text-xs bg-primary text-black font-heading tracking-wider px-4"
            data-testid="submit-post-btn"
          >
            {posting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {uploadingImage ? 'Uploading...' : 'Posting...'}
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5 mr-1.5" /> Post
              </>
            )}
          </Button>
        </div>

        <div className="pt-2 border-t border-border">
          <Link to="/social" className="text-primary hover:underline text-xs font-medium flex items-center gap-1" data-testid="view-social-feed-link">
            <Users className="h-3.5 w-3.5" />
            View Social Feed
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};
